import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles, Compass, BarChart3, User, History, CheckCircle2, XCircle, Lightbulb } from "lucide-react";

function MemoryRow({ m }) {
  const Icon = m.action === "accepted" ? CheckCircle2 : m.action === "rejected" ? XCircle : Sparkles;
  const color = m.action === "accepted" ? "text-emerald-600" : m.action === "rejected" ? "text-destructive" : "text-muted-foreground";
  const when = m.created_at ? new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0" data-testid={`memory-row-${m.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Icon className={`w-4 h-4 ${color} shrink-0`} strokeWidth={1.75} />
        <div className="min-w-0">
          <div className="font-medium truncate">{m.food_name}</div>
          <div className="text-xs text-muted-foreground capitalize">
            {[m.action, m.mood && `· ${m.mood}`, m.weather && `· ${m.weather}`, m.meal_type && `· ${m.meal_type}`].filter(Boolean).join(" ")}
          </div>
        </div>
      </div>
      <div className="text-xs text-muted-foreground shrink-0">{when}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [dna, setDna] = useState(null);
  const [totals, setTotals] = useState(null);
  const [memory, setMemory] = useState([]);
  const [insights, setInsights] = useState([]);
  const [perms, setPerms] = useState(null);
  const [permsDismissed, setPermsDismissed] = useState(false);

  useEffect(() => {
    api.get("/taste-dna").then((r) => setDna(r.data)).catch(() => {});
    api.get("/analytics/overview").then((r) => setTotals(r.data.totals)).catch(() => {});
    api.get("/memory?limit=10").then((r) => setMemory(r.data)).catch(() => {});
    api.get("/analytics/insights").then((r) => setInsights(r.data.insights || [])).catch(() => {});
    api.get("/permissions").then((r) => setPerms(r.data)).catch(() => {});
    if (typeof window !== "undefined") {
      setPermsDismissed(localStorage.getItem("yumo_perm_banner_dismissed") === "1");
    }
  }, []);

  const dismissPermsBanner = () => {
    if (typeof window !== "undefined") localStorage.setItem("yumo_perm_banner_dismissed", "1");
    setPermsDismissed(true);
  };

  const grantAllLearning = async () => {
    try {
      await api.patch("/permissions", { taste_personalization: true, location: true, restaurant_preferences: true });
      const r = await api.get("/permissions");
      setPerms(r.data);
    } catch { /* silent */ }
  };

  const showPermsBanner =
    perms && !permsDismissed && (!perms.taste_personalization || !perms.location);

  // "Here's what YUMO thinks you like" — surfaces once we have ≥ 5 events and top cuisine confidence ≥ 0.4
  const dnaMomentAttrs =
    dna?.event_count >= 5 && dna?.cache?.cuisine
      ? dna.cache.cuisine.filter((r) => r.confidence >= 0.4 && r.preference !== "avoids").slice(0, 3)
      : [];
  const [dnaMomentDismissed, setDnaMomentDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem("yumo_dna_moment_confirmed") === "1"
  );
  const confirmDnaMoment = async (yes) => {
    if (typeof window !== "undefined") localStorage.setItem("yumo_dna_moment_confirmed", "1");
    setDnaMomentDismissed(true);
    if (yes && dnaMomentAttrs.length > 0) {
      // Boost the confirmed cuisines via override — one-tap positive confirmation
      try {
        for (const r of dnaMomentAttrs) {
          await api.patch("/dna/override", { dimension: "cuisine", attribute: r.attribute, action: "boost" });
        }
      } catch { /* silent */ }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 md:pt-8 pb-12" data-testid="dashboard-page">
      <div className="overline mb-2">Today's Food Intelligence</div>
      <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.02]">
        Hey {(() => {
          const first = user?.name?.trim()?.split(" ")[0];
          if (first && !/^yumo$/i.test(first)) return first;
          return user?.email?.split("@")[0] || "friend";
        })()}, <span className="text-muted-foreground italic">what's the mood?</span>
      </h1>

      {/* v5: Progressive permission banner (spec Parts 25, 26) */}
      {showPermsBanner && (
        <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/[0.06] p-4 md:p-5 flex items-start gap-3" data-testid="perms-banner">
          <Lightbulb className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm md:text-base">Let YUMO learn your food preferences</div>
            <div className="text-xs md:text-sm text-muted-foreground mt-1">
              YUMO gets much smarter when it can learn from your activity — location, taste and restaurant picks. You control everything from Profile → Permissions.
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={grantAllLearning} className="rounded-full h-8 px-4 text-xs" data-testid="perms-grant-all-btn">
                Enable learning
              </Button>
              <Link to="/profile" className="text-xs text-primary hover:underline self-center" data-testid="perms-choose-link">Choose granularly →</Link>
              <button onClick={dismissPermsBanner} className="text-xs text-muted-foreground hover:text-foreground ml-auto self-center" data-testid="perms-dismiss-btn">
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v5: "Here's what YUMO thinks you like" (spec Part 25 step 8) */}
      {dnaMomentAttrs.length > 0 && !dnaMomentDismissed && (
        <div className="mt-6 rounded-2xl border border-border bg-secondary/40 p-4 md:p-5" data-testid="dna-moment-card">
          <div className="overline mb-1 text-primary">YUMO thinks you like</div>
          <div className="text-sm md:text-base font-medium">
            {dnaMomentAttrs.map((r) => r.attribute).join(", ")}
            <span className="ml-2 text-xs text-muted-foreground">(based on {dna.event_count} activities)</span>
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => confirmDnaMoment(true)} className="rounded-full h-8 px-4 text-xs" data-testid="dna-moment-yes">
              Yes, that's me
            </Button>
            <Button size="sm" variant="outline" onClick={() => confirmDnaMoment(false)} className="rounded-full h-8 px-4 text-xs" data-testid="dna-moment-no">
              Not really
            </Button>
          </div>
        </div>
      )}

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-8">
        <Card className="md:col-span-8 p-8 rounded-2xl border-border shadow-none bg-foreground text-background relative overflow-hidden" data-testid="cta-recommend-card">
          <div className="overline text-background/60 mb-3">Recommend</div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight max-w-md">
            Open Mother Drive and find your next meal in 10 seconds.
          </h2>
          <Link to="/recommend">
            <Button className="mt-6 rounded-full bg-primary hover:bg-primary/90" data-testid="dash-go-recommend">
              Get a recommendation <ArrowRight className="ml-1 w-4 h-4" strokeWidth={1.75} />
            </Button>
          </Link>
        </Card>

        <Card className="md:col-span-4 p-7 rounded-2xl border-border shadow-none" data-testid="dna-card">
          <Sparkles className="w-5 h-5 text-primary mb-4" strokeWidth={1.5} />
          <div className="overline mb-2">Your Taste DNA</div>
          <div className="font-serif text-3xl font-semibold">{dna?.liked_count ?? 0}</div>
          <div className="text-sm text-muted-foreground">dishes you love</div>
          {dna?.summary?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="dna-summary-chips">
              {dna.summary.slice(0, 3).map((s) => <Badge key={s} variant="outline" className="rounded-full text-[10px]">{s}</Badge>)}
            </div>
          )}
        </Card>

        {/* Food Journey */}
        <Card className="md:col-span-8 p-7 rounded-2xl border-border shadow-none" data-testid="food-journey-card">
          <div className="flex items-center gap-2 mb-1">
            <History className="w-4 h-4 text-primary" strokeWidth={1.75} />
            <div className="overline">Your Food Journey</div>
          </div>
          <h3 className="font-serif text-2xl font-semibold mb-4">Last 10 food decisions</h3>
          {memory.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6" data-testid="memory-empty">
              No decisions yet — get your first recommendation to start your journey.
            </div>
          ) : (
            <div className="divide-y divide-border" data-testid="memory-list">
              {memory.map((m) => <MemoryRow key={m.id} m={m} />)}
            </div>
          )}
        </Card>

        {/* Insights */}
        <Card className="md:col-span-4 p-7 rounded-2xl border-border shadow-none bg-primary/[0.04]" data-testid="insights-card">
          <Lightbulb className="w-5 h-5 text-primary mb-3" strokeWidth={1.75} />
          <div className="overline mb-2">YUMO Insights</div>
          {insights.length === 0 ? (
            <div className="text-sm text-muted-foreground">Insights appear as you use YUMO.</div>
          ) : (
            <ul className="space-y-2.5 text-sm">
              {insights.map((s, i) => (
                <li key={i} className="flex gap-2"><span className="text-primary">→</span><span>{s}</span></li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="md:col-span-4 p-7 rounded-2xl border-border shadow-none">
          <Compass className="w-5 h-5 text-primary mb-4" strokeWidth={1.5} />
          <div className="overline mb-2">Food Universe</div>
          <div className="font-serif text-3xl font-semibold">{totals?.foods ?? 0}</div>
          <div className="text-sm text-muted-foreground">dishes ready to discover</div>
          <Link to="/explorer"><Button variant="ghost" size="sm" className="mt-3 px-0" data-testid="dash-go-explorer">Explore →</Button></Link>
        </Card>

        <Card className="md:col-span-4 p-7 rounded-2xl border-border shadow-none">
          <BarChart3 className="w-5 h-5 text-primary mb-4" strokeWidth={1.5} />
          <div className="overline mb-2">Analytics</div>
          <div className="font-serif text-3xl font-semibold">{totals?.recommendations ?? 0}</div>
          <div className="text-sm text-muted-foreground">recommendations issued</div>
          <Link to="/analytics"><Button variant="ghost" size="sm" className="mt-3 px-0" data-testid="dash-go-analytics">Open dashboard →</Button></Link>
        </Card>

        <Card className="md:col-span-4 p-7 rounded-2xl border-border shadow-none">
          <User className="w-5 h-5 text-primary mb-4" strokeWidth={1.5} />
          <div className="overline mb-2">Profile</div>
          <div className="text-sm text-foreground/85 leading-snug">
            {user?.profile?.diet_type ? (
              <>You're set as <span className="font-medium capitalize">{user.profile.diet_type}</span> · {user.profile.spice_preference || "any spice"}.</>
            ) : (
              <>Set diet, spice & favorites to sharpen Mother Drive.</>
            )}
          </div>
          <Link to="/profile"><Button variant="ghost" size="sm" className="mt-3 px-0" data-testid="dash-go-profile">Edit profile →</Button></Link>
        </Card>
      </div>
    </div>
  );
}
